import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Select from 'react-select';
import Loading from '../../../../shared/loading';
import { selectorProps, InputText, HelpBlock } from '../../common/form';
import { usePostQuestion, usePutQuestion } from '../../../../services/lwadmin/fund/question-service';
import useStore from './store-hook';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  font: ${props => props.theme.fontWeightHeavy} 14px/120% ${props => props.theme.fontFamilyBody};
  margin: 10px 0;
  padding: 5px 10px;
  border-radius: 4px;
  box-shadow: 0px 2px 9px rgba(0, 0, 0, 0.25);
  border: 1px solid ${props => props.theme.secondaryColorLight};
  background-color: ${props => props.theme.secondaryColorLightest};

  & [class$="-ValueContainer"] {
    margin-top: 0px !important;

    [class$="-singleValue"], [class$="-multiValue"] {
      font-weight: ${props => props.theme.fontWeightHeavy};
    }
  }

  & > .category {
    width: 280px;
    margin-right: 20px;
    font-size: 12px;
    text-transform: uppercase;
    color: ${props => props.theme.secondaryColor};
  }

  & > label {
    width: 160px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  & > .question {
    flex-basis: 100%;
    color: ${props => props.theme.bodyColor};
    font-size: 18px;
    font-weight: ${props => props.theme.fontWeightHeavy};
    margin: 10px 0;
  }

  & > .types {
    width: 500px;
    margin-left: 10px;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    > [class$="-control"] {
      width: 100%;
    }

    > span {
      margin-left: 10px;
      background-color: ${props => props.theme.secondaryColorLight};
      border-radius: 3px;
      border: 1px solid ${props => props.theme.primaryColor};
      padding: 2px 5px;
      font-size: 12px;
    }
  }

  & > .fa-edit {
    margin-right: -40px;
    margin-left: 20px;
    font-weight: ${props => props.theme.fontWeightBlack};
    font-size: 20px;
    color: ${props => props.theme.primaryColor};
  }

  & > .btn-group {
    margin: 0 !important;
    display: flex;
    flex-basis: 100%;
    justify-content: flex-end;

    > button, > .loading-panel {
      margin-left: 20px;
    }
  }
`;

const plainMapper = value => ({ value, label: value });

export default function Editor({
  question, onCancel, onUpdated, onCreated,
}) {
  const [err, setErr] = React.useState(false);
  const loadingRef = React.useRef();
  const [{ loading: uloading }, updateQ] = usePutQuestion();
  const [{ loading: cloading }, createQ] = usePostQuestion();
  const cats = useStore(state => state.cats);
  const [q, setQ] = React.useState(question);

  // React.useEffect(() => setQ(question), [question]);

  React.useEffect(() => {
    loadingRef.current && loadingRef.current.toggle(cloading || uloading);
  }, [cloading, uloading]);

  React.useEffect(() => setErr(false), [q]);

  const cancel = e => {
    e && e.preventDefault();
    setQ(question);
    onCancel();
  };

  const save = e => {
    e && e.preventDefault();
    (q.id ? updateQ : createQ)({
      ...q,
      catId: q.category.value,
      fundTypes: q.fundTypes.map(t => t.value),
    }).then(result => {
      (q.id ? onUpdated : onCreated)({ ...q, id: q.id || result.id });
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.log(`Error saving question: ${JSON.stringify(error)}`);
      setErr(true);
    });
  };

  return (
    <Container>
      <Select
        {...selectorProps}
        className="category"
        value={q.category}
        options={cats}
        onChange={d => setQ({ ...q, category: d })}
      />
      <label htmlFor="order">
        {`Order: ${q.order}`}
        <input
          id="order"
          type="range"
          min="1"
          max="100"
          value={q.order}
          onChange={d => setQ({ ...q, order: d.target.value })}
        />
      </label>
      <Select
        {...selectorProps}
        isMulti
        className="types"
        value={q.fundTypes}
        options={['ETF', 'LIC', 'LIT', 'Managed Fund'].map(plainMapper)}
        onChange={d => setQ({ ...q, fundTypes: d })}
      />
      <i className="fa fa-edit" />
      <InputText
        type="text"
        value={q.description}
        className="question"
        onChange={d => setQ({ ...q, description: d.target.value })}
      />
      {!!err && <HelpBlock>Failed to save question.</HelpBlock>}
      <div className="btn-group">
        <button type="button" className="btn btn-secondary" onClick={cancel}>Cancel</button>
        <Loading variant="button" ref={loadingRef}>
          <button type="button" className="btn btn-primary" onClick={save}>Save</button>
        </Loading>
      </div>
    </Container>
  );
}

Editor.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
    description: PropTypes.string,
    fundTypes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
    order: PropTypes.number,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  onCreated: PropTypes.func,
};

Editor.defaultProps = {
  onUpdated: () => null,
  onCreated: () => null,
};
